import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

const LogoSVG = () => (
  <img
    src={logo}
    alt="Accelrix Logo"
    className="h-10 w-auto"
    width="160"
    height="40"
    decoding="async"
  />
);

const Navbar = () => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Function to toggle the mobile menu state
  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  // Function to close the mobile menu
  const closeMenu = () => {
    setIsOpen(false);
  };

  // Keep transparent style only at the top of the home page.
  useEffect(() => {
    if (pathname !== "/") {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  // Close mobile menu whenever route changes.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Prevent background scroll while mobile menu is open.
  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Internships", path: "/internships" },
    { name: "Services", path: "/services" },
    { name: "Features", path: "/features" },
    { name: "About", path: "/about" },
    { name: "FAQs", path: "/faqs" },
    { name: "Verify", path: "/verify" },
    { name: "Contact", path: "/contact" },
  ];

  const isLinkActive = (path) => {
    if (path === "/services") {
      return pathname === "/services" || pathname === "/servies";
    }

    if (path === "/verify") {
      return pathname === "/verify" || pathname.startsWith("/verify/");
    }

    return pathname === path;
  };

  return (
    <nav
      className={`fixed w-full z-50 py-4 px-4 sm:px-6 lg:px-10 top-0 transition-all duration-300 ease-in-out
                   ${
                     isScrolled || isOpen
                       ? "bg-white/80 dark:bg-dark-bg-secondary/80 backdrop-blur-sm shadow-sm"
                       : "bg-transparent"
                   }`}
    >
      <div className="container mx-auto flex justify-between items-center">
        {/* Logo */}
        <Link
          to="/"
          onClick={closeMenu}
          className="flex items-center space-x-2 text-2xl font-bold group"
        >
          <LogoSVG />
          <span className="text-4xl font-extrabold bg-gradient-to-r from-logo-gradient-start to-logo-gradient-end text-transparent bg-clip-text transform group-hover:scale-105 transition-transform duration-200">
            Accelrix
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex space-x-2 items-center">
          <ul className="flex space-x-2">
            {navLinks.map((link) => {
              const isActive = isLinkActive(link.path);
              return (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={closeMenu}
                    className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 whitespace-nowrap
                                 ${
                                   isActive
                                     ? "text-accelrix-blue font-bold after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accelrix-blue after:rounded-full after:transition-all after:duration-300"
                                     : "text-light-text-secondary hover:text-light-text-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accelrix-blue after:rounded-full after:transition-all after:duration-300 hover:after:w-full"
                                 }`}
                  >
                    {link.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center">
          <button
            onClick={toggleMenu}
            className="p-2.5 bg-transparent border-0 text-light-text-secondary dark:text-dark-text-secondary hover:text-accelrix-blue dark:hover:text-accelrix-blue transition-colors duration-300 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
            aria-label="Toggle navigation"
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            {isOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                className="h-7 w-7 fill-current"
              >
                <path d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                className="h-7 w-7 fill-current"
              >
                <path d="M0 96C0 78.3 14.3 64 32 64H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H416c17.7 0 32 14.3 32 32z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-navigation"
        className={`md:hidden absolute left-0 w-full bg-white dark:bg-dark-bg-secondary shadow-lg py-2 overflow-hidden transition-all duration-500 ease-in-out
                    ${
                      isOpen
                        ? "max-h-screen opacity-100 pointer-events-auto"
                        : "max-h-0 opacity-0 pointer-events-none"
                    }`}
        style={{ top: "100%", left: "0" }}
      >
        <ul className="flex flex-col space-y-2 px-4 py-2">
          {navLinks.map((link) => {
            const isActive = isLinkActive(link.path);
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  onClick={closeMenu}
                  className={`block px-3 py-2 rounded-lg text-lg font-medium transition-colors duration-300
                               ${
                                 isActive
                                   ? "bg-accelrix-blue text-white"
                                   : "text-light-text-primary hover:bg-light-bg-primary dark:text-dark-text-primary dark:hover:bg-dark-bg-primary"
                               }`}
                >
                  {link.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
